/**
 * SDK 酒店信息解析(请求层 B3 + 安全校验 B4)
 *
 * 镜像 hotel-hub 的 ensureHotelId / fetchHotelInfo 逻辑,但:
 * - 不依赖 hotel-hub 的 zustand store(SDK 独立 bundle,无该 store)
 * - 用完整域名查询(与 hotel-hub SSR fetchHotelInfo 一致)
 * - 缓存复用 shared-storage 的 readHotelCache/writeHotelCache(同源 sessionStorage,
 *   与 hotel-hub 浏览器侧互通;域名 key 差异由 readHotelCache 的 last-domain 兜底桥接)
 *
 * hotel/info 是公开接口(按域名查酒店,不需 token、不需 hotelId 注入),
 * 故此处直接 raw fetch 同源 BFF /api/hotel/info,不经过 client.ts 的注入层 -> 避免循环依赖与递归。
 * (BFF 内 callUpstream('hotelInfo') 复用 hotel-hub 服务端 fetchHotelInfo 的 5min 缓存。)
 */
import type { HotelInfo } from '@/types/hotel';
import { readHotelCache, writeHotelCache } from '@/lib/shared-storage';
import { getConfig } from '../config';

const HOTEL_INFO_TIMEOUT = 15000;
/** 同源 BFF 路径（hotel-hub app/api/hotel/info），替代直连 whapi /api/hotel/offical/hotel/info */
const HOTEL_INFO_BFF_PATH = '/api/hotel/info';

/**
 * 提取完整域名(去协议/路径/端口,镜像 hotel-hub src/lib/api/hotel.ts,与 SSR 路径一致)。
 * www.hotel.com -> www.hotel.com;hotel.com -> hotel.com。
 * 单段标识(如 config.domain='AAA111')原样保留大小写。
 */
export function getFullDomain(host: string): string {
  if (!host) return '';
  // 测试环境单段标识(如 ?domain=AJJLJD)非真实主机名:原样返回,保留大小写
  if (!host.includes('.')) return host.replace(/^https?:\/\//, '').split(/[/:]/)[0];
  // 真实主机名用 URL 解析,自动剥离协议/路径/端口并小写化
  try {
    return new URL(host.includes('://') ? host : `https://${host}`).hostname;
  } catch {
    return host;
  }
}

/** 当前站点完整域名(供 hotel/info 查询与缓存 key)。
 *  config.domain 覆盖时直接采用(本地/测试用真实域名如 AJJLJD 模拟),否则从 location.host 推导。 */
export function getDomain(): string {
  const { domain } = getConfig();
  if (domain) return domain;
  if (typeof location === 'undefined') return '';
  return getFullDomain(location.host);
}

/** 酒店信息并发去重锁(多实例/多请求共享同一次 fetch) */
let hotelFetching: Promise<HotelInfo | null> | null = null;

/**
 * 解析当前域对应的酒店信息(缓存 + 并发去重)。
 *
 * 安全校验语义:返回 null 表示当前域名未配置酒店/未授权,
 * 调用方(Web Component)应挂 hidden 不渲染组件,并派发 hotelerror 事件。
 */
export async function resolveHotel(): Promise<HotelInfo | null> {
  const domain = getDomain();
  const cached = readHotelCache(domain);
  if (cached?.id) return cached;

  if (!hotelFetching) {
    hotelFetching = (async () => {
      try {
        const { basePath } = getConfig();
        // 同源 BFF：hotel/info 改走宿主同源 /hub/api/hotel/info（BFF 服务端按 domain 解析），
        // 不再跨源直连 whapi（消除 SDK 集成方域名 CORS 白名单）。
        // dev/test 透传 ?domain= 给 BFF（proxy 据此注入 x-hotel-domain）；生产走子域名，BFF 用 host 头。
        // 对齐 customer-service.ts / coupons.ts 的 BFF 调用方式。
        const url = `${basePath}${HOTEL_INFO_BFF_PATH}${
          domain ? `?domain=${encodeURIComponent(domain)}` : ''
        }`;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), HOTEL_INFO_TIMEOUT);
        let res: Response;
        try {
          res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ domain }),
            signal: controller.signal,
          });
        } finally {
          clearTimeout(timer);
        }
        if (!res.ok) return null;
        const data = (await res.json()) as HotelInfo;
        if (data?.id) {
          writeHotelCache(data, domain);
          return data;
        }
        return null;
      } catch {
        return null;
      } finally {
        hotelFetching = null;
      }
    })();
  }
  return hotelFetching;
}

/**
 * 获取 hotelId(供请求层注入到业务接口)。
 *
 * 一律由 resolveHotel() 按域名解析得到--不提供 hotelId 捷径,避免绕过域名校验:
 * 未授权域名 resolveHotel 返回 null,组件挂 hidden 不渲染并派发 hotelerror,业务接口也不会拿到 hotelId。
 */
export async function ensureHotelId(): Promise<number | null> {
  return (await resolveHotel())?.id ?? null;
}
